import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const err = (e) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : "Request failed"; };
const TONE = { APPROVED: "bg-emerald-100 text-emerald-700", PENDING: "bg-amber-100 text-amber-800", REJECTED: "bg-rose-100 text-rose-700", PAUSED: "bg-slate-200 text-slate-700", DISABLED: "bg-slate-200 text-slate-700" };
const EMPTY = { name: "", language: "en_GB", category: "UTILITY", header: "", body: "", footer: "", examples: "", buttons: "" };

function NewTemplate({ onDone }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const vars = new Set((f.body.match(/\{\{(\d+)\}\}/g) || [])).size;
  const submit = async () => {
    setBusy(true);
    const split = (v) => v.split("|").map((x) => x.trim()).filter(Boolean);
    try {
      await api.post("/wa/cloud/templates", { ...f, header: f.header || null, footer: f.footer || null, examples: split(f.examples), buttons: split(f.buttons) });
      toast.success("Sent to Meta for approval"); setF(EMPTY); onDone();
    } catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };
  return (
    <section className="bg-white border border-slate-200 rounded-2xl p-5 space-y-3" data-testid="wa-tpl-form">
      <h3 className="font-semibold text-slate-900">New template</h3>
      <div className="grid sm:grid-cols-3 gap-2">
        <Input placeholder="name_like_this" value={f.name} onChange={set("name")} data-testid="wa-tpl-name" />
        <Input placeholder="en_GB" value={f.language} onChange={set("language")} data-testid="wa-tpl-language" />
        <select className="border rounded-md text-sm px-2" value={f.category} onChange={set("category")} data-testid="wa-tpl-category">
          <option value="UTILITY">Utility</option><option value="MARKETING">Marketing</option><option value="AUTHENTICATION">Authentication</option></select>
      </div>
      <Input placeholder="Header (optional, 60 characters)" value={f.header} onChange={set("header")} data-testid="wa-tpl-header" />
      <Textarea placeholder="Body, e.g. Hi {{1}}, your service is booked for {{2}}." value={f.body} onChange={set("body")} data-testid="wa-tpl-body" />
      {vars > 0 && <Input placeholder={`Example values for ${vars} variable(s), separated by |`} value={f.examples} onChange={set("examples")} data-testid="wa-tpl-examples" />}
      <div className="grid sm:grid-cols-2 gap-2">
        <Input placeholder="Footer (optional)" value={f.footer} onChange={set("footer")} data-testid="wa-tpl-footer" />
        <Input placeholder="Quick-reply buttons, separated by | (max 3)" value={f.buttons} onChange={set("buttons")} data-testid="wa-tpl-buttons" />
      </div>
      <Button size="sm" disabled={busy || !f.name || !f.body} onClick={submit} data-testid="wa-tpl-submit">{busy && <Loader2 className="w-4 h-4 animate-spin mr-1" />}Submit for approval</Button>
    </section>
  );
}

export const WaTemplatesTab = () => {
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/wa/cloud/templates").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const sync = async () => { setBusy(true); try { const r = await api.post("/wa/cloud/templates/sync"); toast.success(`${r.data.synced} templates refreshed from Meta`); load(); } catch (e) { toast.error(err(e)); } finally { setBusy(false); } };
  const del = async (name) => { if (!window.confirm(`Delete ${name} from Meta? This removes every language of it.`)) { return; } try { await api.delete(`/wa/cloud/templates/${name}`); toast.success("Deleted"); load(); } catch (e) { toast.error(err(e)); } };
  const manage = can("whatsapp.templates.manage");
  return (
    <div className="space-y-4" data-testid="wa-templates-tab">
      <section className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-3"><h3 className="font-semibold text-slate-900">Message templates</h3>
          {manage && <Button size="sm" variant="outline" className="ml-auto" disabled={busy} onClick={sync} data-testid="wa-tpl-sync"><RefreshCw className="w-4 h-4 mr-1" />Refresh status from Meta</Button>}</div>
        <div className="divide-y" data-testid="wa-tpl-list">{rows.map((t) => (
          <div key={t.name + t.language} className="py-2 flex flex-wrap items-center gap-2 text-sm" data-testid={`wa-tpl-row-${t.name}`}>
            <b className="text-slate-900">{t.name}</b><span className="text-xs text-slate-500">{t.language} · {t.category}</span>
            <span className={`text-xs rounded-full px-2 py-0.5 ${TONE[t.status] || "bg-slate-100 text-slate-600"}`} data-testid={`wa-tpl-status-${t.name}`}>{t.status}</span>
            {t.reason && t.reason !== "NONE" && <span className="text-xs text-rose-700">{t.reason}</span>}
            <span className="basis-full text-xs text-slate-500 truncate">{t.body}</span>
            {manage && <Button size="sm" variant="ghost" className="ml-auto text-rose-700" onClick={() => del(t.name)} data-testid={`wa-tpl-delete-${t.name}`}><Trash2 className="w-4 h-4" /></Button>}
          </div>))}
          {!rows.length && <p className="text-sm text-slate-400 py-2">No templates yet.</p>}</div>
      </section>
      {manage && <NewTemplate onDone={load} />}
    </div>
  );
};
